import { Injectable, signal } from '@angular/core';

export interface IToast {
  id: number;
  title?: string;
  description?: string;
  sourceId?: string;
}

const AUTO_DISMISS_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class ToasterService {
  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  // In-memory only: dismissed IDs are cleared on every page reload so all
  // notifications reappear fresh with a new 60s timer.
  private readonly dismissedIds = new Set<string>();

  readonly toasts = signal<IToast[]>([]);

  // Called by the auto-dismiss timer — removes toast visually only.
  private expire(id: number): void {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
    this.toasts.update(list => list.filter(t => t.id !== id));
  }

  isDismissed(sourceId: string): boolean {
    return this.dismissedIds.has(sourceId);
  }

  add(toast: Omit<IToast, 'id'>): void {
    const id = this.nextId++;
    this.toasts.update(list => [...list, { ...toast, id }]);
    const timer = setTimeout(() => this.expire(id), AUTO_DISMISS_MS);
    this.timers.set(id, timer);
  }

  // Called by the close button — removes toast and marks sourceId as dismissed
  // for the current page load only (in-memory, cleared on reload).
  dismiss(id: number): void {
    const toast = this.toasts().find(t => t.id === id);
    if (toast?.sourceId) {
      this.dismissedIds.add(toast.sourceId);
    }
    this.expire(id);
  }

  clear(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.toasts.set([]);
  }
}
